/**
 * GoodsDisplayBoard - 商品ディスプレイボード表示コンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see docs/VISUAL_BLUEPRINT.md Section 2.4
 * @see src/types/ui-interfaces.ts
 */

import type { GoodsColor } from '../../types';
import type { GoodsDisplayBoardProps } from '../../types/ui-interfaces';
import { useTranslations, interpolate } from '../../i18n';
import { useIsMobile } from '../../hooks/useIsMobile';

// GoodsDisplayBoardProps を再エクスポート（後方互換性のため）
export type { GoodsDisplayBoardProps } from '../../types/ui-interfaces';

/**
 * 商品色に応じたTailwindクラスを返す
 */
function getGoodsColorClass(color: GoodsColor | null): string {
  switch (color) {
    case 'red':
      return 'bg-goods-red';
    case 'blue':
      return 'bg-goods-blue';
    case 'yellow':
      return 'bg-goods-yellow';
    case 'purple':
      return 'bg-goods-purple';
    case 'black':
      return 'bg-goods-black';
    default:
      return 'bg-muted';
  }
}

/**
 * 商品キューブの表示（通常モード）
 */
function GoodsCube({ color }: { color: GoodsColor | null }) {
  if (color === null) {
    return <div className="w-4 h-4 md:w-6 md:h-6 rounded-sm border border-dashed border-muted-foreground/30 bg-muted/20" />;
  }
  const borderClass = color === 'black' ? 'border-white' : 'border-foreground/20';
  return (
    <div
      className={`w-4 h-4 md:w-6 md:h-6 rounded-sm shadow-sm border ${borderClass} ${getGoodsColorClass(color)}`}
      data-goods-color={color}
    />
  );
}

/**
 * クリック可能な商品キューブセル（Production モード用）
 */
interface ClickableCellProps {
  color: GoodsColor | null;
  isSelected: boolean;
  isClickable: boolean;
  onClick: () => void;
}

function ClickableCell({ color, isSelected, isClickable, onClick }: ClickableCellProps) {
  if (color !== null) {
    // 既にキューブがある場合は通常表示
    const borderClass = color === 'black' ? 'border-white' : 'border-foreground/20';
    return (
      <div
        className={`w-4 h-4 md:w-6 md:h-6 rounded-sm shadow-sm border ${borderClass} ${getGoodsColorClass(color)}`}
        data-goods-color={color}
      />
    );
  }

  // 空きセル：クリック可能な場合はインタラクティブに
  return (
    <div
      className={`
        w-4 h-4 md:w-6 md:h-6 rounded-sm border transition-all duration-150
        ${isClickable
          ? 'border-primary/50 bg-primary/10 cursor-pointer hover:bg-primary/20 hover:border-primary'
          : 'border-dashed border-muted-foreground/30 bg-muted/20'}
        ${isSelected ? 'ring-2 ring-primary bg-primary/20' : ''}
      `}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={(e) => isClickable && e.key === 'Enter' && onClick()}
    />
  );
}

/**
 * 都市列の表示（Production モード対応）
 */
interface CityColumnProps {
  side: 'light' | 'dark';
  columnNumber: number;
  cubes: (GoodsColor | null)[];
  cityId: string;
  productionMode?: GoodsDisplayBoardProps['productionMode'];
  isCompact?: boolean;
}

function CityColumn({
  side,
  columnNumber,
  cubes,
  cityId: _cityId,
  productionMode,
  isCompact = false,
}: CityColumnProps) {
  const isCellSelected = (row: number) => {
    if (!productionMode?.selectedCell) return false;
    return (
      productionMode.selectedCell.side === side &&
      productionMode.selectedCell.column === columnNumber &&
      productionMode.selectedCell.columnType === 'city' &&
      productionMode.selectedCell.row === row
    );
  };

  // セルがクリック可能かどうか（商品キューブが選択されている場合のみ）
  const isCellClickable = (cube: GoodsColor | null) => {
    return cube === null && productionMode?.selectedGoodsIndex !== null;
  };

  return (
    <div
      className={`
        flex flex-col items-center rounded transition-all duration-150
        ${isCompact ? 'gap-0.5 p-0.5' : 'gap-1 p-2'}
        ${side === 'dark' ? 'bg-foreground/5' : 'bg-card'}
      `}
    >
      <div className={`
        ${isCompact ? 'w-5 h-5 text-[10px]' : 'w-8 h-8 text-sm'} rounded-full flex items-center justify-center font-heading font-bold
        ${side === 'dark' ? 'bg-foreground text-background' : 'bg-card text-foreground border border-border'}
      `}>
        {columnNumber}
      </div>
      <div className={`flex flex-col ${isCompact ? 'gap-0.5' : 'gap-1'}`}>
        {cubes.map((cube, index) => (
          productionMode ? (
            <ClickableCell
              key={index}
              color={cube}
              isSelected={isCellSelected(index)}
              isClickable={isCellClickable(cube)}
              onClick={() => productionMode.onCellClick(side, columnNumber, 'city', index)}
            />
          ) : (
            <GoodsCube key={index} color={cube} />
          )
        ))}
      </div>
    </div>
  );
}

/**
 * 新都市IDに対応する背景色クラスを返す
 * A: Red, B: Blue, C/D: Black, E-H: Black (Barbados未使用)
 */
function getNewCityBgClass(newCityId: string): string {
  switch (newCityId) {
    case 'A':
      return 'bg-goods-red';
    case 'B':
      return 'bg-goods-blue';
    case 'E':
      return 'bg-goods-yellow';
    case 'F':
      return 'bg-goods-purple';
    default:
      return 'bg-goods-black';
  }
}

/**
 * 新都市列の表示（Production モード対応）
 */
interface NewCityColumnProps {
  side: 'light' | 'dark';
  columnNumber: number;
  cubes: (GoodsColor | null)[];
  newCityId: string;
  productionMode?: GoodsDisplayBoardProps['productionMode'];
  isCompact?: boolean;
}

function NewCityColumn({
  side,
  columnNumber,
  cubes,
  newCityId,
  productionMode,
  isCompact = false,
}: NewCityColumnProps) {
  const isCellSelected = (row: number) => {
    if (!productionMode?.selectedCell) return false;
    return (
      productionMode.selectedCell.side === side &&
      productionMode.selectedCell.column === columnNumber &&
      productionMode.selectedCell.columnType === 'newCity' &&
      productionMode.selectedCell.row === row
    );
  };

  // セルがクリック可能かどうか（商品キューブが選択されている場合のみ）
  const isCellClickable = (cube: GoodsColor | null) => {
    return cube === null && productionMode?.selectedGoodsIndex !== null;
  };

  return (
    <div
      className={`
        flex flex-col items-center rounded transition-all duration-150
        ${isCompact ? 'gap-0.5 p-0.5' : 'gap-1 p-2'}
        ${side === 'dark' ? 'bg-foreground/10' : 'bg-muted/50'}
      `}
    >
      <div
        className={`${isCompact ? 'w-5 h-5 text-[10px]' : 'w-8 h-8 text-sm'} rounded flex items-center justify-center font-heading font-bold text-white ${getNewCityBgClass(newCityId)}`}
        style={{
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
        }}
      >
        {newCityId}
      </div>
      <div className={`flex flex-col ${isCompact ? 'gap-0.5' : 'gap-1'}`}>
        {cubes.map((cube, index) => (
          productionMode ? (
            <ClickableCell
              key={index}
              color={cube}
              isSelected={isCellSelected(index)}
              isClickable={isCellClickable(cube)}
              onClick={() => productionMode.onCellClick(side, columnNumber, 'newCity', index)}
            />
          ) : (
            <GoodsCube key={index} color={cube} />
          )
        ))}
      </div>
    </div>
  );
}

/**
 * 商品ディスプレイボード
 *
 * 構造:
 * - Light Side（明側）: 都市列1-6 + 新都市列A-D
 * - Dark Side（暗側）: 都市列1-6 + 新都市列E-H
 */
export function GoodsDisplayBoard({
  goodsDisplay,
  onClose,
  productionMode,
  hideDarkSide = false,
  rightAlignModal = false,
}: GoodsDisplayBoardProps) {
  const t = useTranslations();

  // 都市列と新都市列を分離
  const getCityColumns = (side: 'light' | 'dark') => {
    const columns = side === 'light' ? goodsDisplay.lightSide : goodsDisplay.darkSide;
    return columns.filter((col) => col.cityId !== '' && col.newCityId === null);
  };

  const getNewCityColumns = (side: 'light' | 'dark') => {
    const columns = side === 'light' ? goodsDisplay.lightSide : goodsDisplay.darkSide;
    return columns.filter((col) => col.newCityId !== null);
  };

  // 縦幅500px未満をスマホ（コンパクト）として判定
  const isMobile = useIsMobile();

  // 右寄せ＋背面見やすく（Barbados/Detroit共通）
  const isRightAligned = rightAlignModal || productionMode || hideDarkSide;

  return (
    <div className={`fixed inset-0 z-50 flex ${isRightAligned ? 'items-start justify-end' : 'items-center justify-center'}`} role="dialog" aria-modal="true" aria-labelledby="goods-display-title">
      {/* オーバーレイ背景 - productionMode時は薄く */}
      <div className={`absolute inset-0 ${isRightAligned ? 'bg-foreground/20' : 'bg-foreground/50 backdrop-blur-sm'}`} onClick={onClose} />

      {/* モーダルコンテンツ - 右上配置の場合はmt-4 mr-4 */}
      <div className={`relative bg-card rounded-lg shadow-xl w-fit ${isRightAligned ? 'mt-4 mr-4' : 'mx-2 md:mx-4'} ${isMobile ? 'p-1.5' : 'p-3 md:p-6'} animate-scale-in max-h-[90vh] overflow-y-auto`}>
        {/* ヘッダー */}
        <div className={`flex justify-between items-center ${isMobile ? 'mb-1 pb-1' : 'mb-2 md:mb-4 pb-2 md:pb-3'} border-b border-border`}>
          <h2 id="goods-display-title" className="font-heading text-base md:text-xl font-semibold text-foreground">
            {productionMode ? t.goodsDisplay.production : t.goodsDisplay.title}
          </h2>
          <div className="flex items-center gap-1 md:gap-2">
            {/* 凡例（ヘッダー内に右詰め、スマホでは非表示） */}
            {!isMobile && (
              <div className="flex gap-[2px] flex-wrap">
                <span className="flex items-center gap-0.5 text-[10px] md:text-xs"><span className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-goods-red" /></span>
                <span className="flex items-center gap-0.5 text-[10px] md:text-xs"><span className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-goods-blue" /></span>
                <span className="flex items-center gap-0.5 text-[10px] md:text-xs"><span className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-goods-yellow" /></span>
                <span className="flex items-center gap-0.5 text-[10px] md:text-xs"><span className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-goods-black" /></span>
                <span className="flex items-center gap-0.5 text-[10px] md:text-xs"><span className="w-3 h-3 md:w-4 md:h-4 rounded-sm bg-goods-purple" /></span>
              </div>
            )}
            <button className="btn-ghost px-2 py-0.5 md:px-3 md:py-1 text-xs md:text-sm" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Production モード: 引いた商品の表示と操作UI（hideDarkSideでない場合のみここに表示） */}
        {productionMode && !hideDarkSide && (
          <div className="mb-2 md:mb-4 p-2 md:p-4 bg-primary/10 rounded-lg border border-primary/30">
            {/* 説明テキスト */}
            <div className="text-xs md:text-sm font-medium text-foreground mb-2">
              {productionMode.selectedGoodsIndex !== null
                ? t.goodsDisplay.selectCell
                : t.goodsDisplay.selectCube}
            </div>

            {/* 配置するキューブの表示（クリック可能） */}
            <div className="flex flex-wrap gap-2 md:gap-3 items-center mb-2 md:mb-3">
              {productionMode.drawnGoods.map((color, index) => {
                const isPlaced = productionMode.placedFlags[index];
                const isSelected = productionMode.selectedGoodsIndex === index;
                // 未配置のキューブは常にクリック可能（選択切り替え可能）
                const isClickable = !isPlaced;

                return (
                  <button
                    key={index}
                    className={`
                      flex flex-col items-center p-1 rounded transition-all
                      ${isClickable ? 'cursor-pointer hover:bg-primary/20' : ''}
                      ${isSelected ? 'bg-primary/30 ring-2 ring-primary' : ''}
                      ${isPlaced ? 'opacity-50' : ''}
                    `}
                    onClick={() => isClickable && productionMode.onGoodsClick(index)}
                    disabled={isPlaced}
                  >
                    <div className="relative">
                      <div
                        className={`
                          w-6 h-6 md:w-8 md:h-8 rounded-sm shadow-sm border-2 transition-all
                          ${getGoodsColorClass(color)}
                          ${isSelected ? 'border-primary' : 'border-foreground/20'}
                          ${isPlaced ? 'opacity-50' : ''}
                        `}
                      />
                      {isPlaced && (
                        <span className="absolute inset-0 flex items-center justify-center text-xs md:text-sm font-bold text-white bg-foreground/40 rounded-sm">✓</span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-primary text-[10px] md:text-xs mt-0.5 font-medium">{t.goodsDisplay.selected}</span>
                    )}
                  </button>
                );
              })}

              <span className="ml-2 md:ml-4 text-xs md:text-sm text-muted-foreground">
                {interpolate(t.goodsDisplay.placed, { count: productionMode.placedCount })}
              </span>
            </div>

            {/* ボタン群 */}
            <div className="flex flex-wrap gap-2">
              {/* Confirm: 商品選択済み + セル選択済みの場合のみ有効 */}
              <button
                className="btn-primary text-xs md:text-sm"
                onClick={productionMode.onConfirmPlacement}
                disabled={productionMode.selectedGoodsIndex === null || !productionMode.selectedCell}
              >
                {t.common.confirm}
              </button>

              {/* Cancel: 配置済みが1つ以上ある場合のみ有効 */}
              <button
                className="btn-ghost text-xs md:text-sm"
                onClick={productionMode.onCancelPlacement}
                disabled={productionMode.placedCount === 0 && productionMode.selectedGoodsIndex === null && !productionMode.selectedCell}
              >
                {t.common.cancel}
              </button>

              {/* Finish: 2個とも配置済みの場合のみ有効 */}
              <button
                className="btn-secondary text-xs md:text-sm"
                onClick={productionMode.onFinishPlacement}
                disabled={productionMode.placedCount < 2}
              >
                {t.common.finish}
              </button>
            </div>
          </div>
        )}

        {/* ボードコンテンツ */}
        <div className="inline-flex gap-0.5">
          {/* Light Side */}
          <div className={isMobile ? '' : 'space-y-1 md:space-y-2'}>
            {/* ライトサイドヘッダー（hideDarkSideの場合は非表示） */}
            {!hideDarkSide && (
              <h3 className="font-heading text-xs md:text-sm font-semibold text-foreground bg-muted/50 px-1.5 py-0.5 md:px-2 md:py-1 rounded">
                {t.goodsDisplay.lightSide}
              </h3>
            )}

            {/* 6列グリッド: 1-6 の下に A-D（3-6の位置） */}
            <div className="grid grid-cols-6 gap-0.5">
              {/* 都市列 1-6 */}
              {getCityColumns('light').map((col) => (
                <CityColumn
                  key={`city-${col.columnNumber}`}
                  side="light"
                  columnNumber={col.columnNumber}
                  cubes={col.cubes}
                  cityId={col.cityId}
                  productionMode={productionMode}
                  isCompact={isMobile}
                />
              ))}
              {/* 新都市列 A-D: 3-6の下に配置（col-start-3） */}
              <div className="col-span-2" /> {/* 1-2の下は空 */}
              {getNewCityColumns('light').map((col) => (
                <NewCityColumn
                  key={`newcity-${col.newCityId}`}
                  side="light"
                  columnNumber={col.columnNumber}
                  cubes={col.cubes}
                  newCityId={col.newCityId!}
                  productionMode={productionMode}
                  isCompact={isMobile}
                />
              ))}
            </div>
          </div>

          {/* Dark Side または 操作パネル（hideDarkSideの場合） */}
          {hideDarkSide ? (
            // ソロプレイ: 操作パネルをダークサイドの位置に表示
            productionMode ? (
              <div className={`bg-primary/10 rounded border border-primary/30 ${isMobile ? 'p-1 w-[120px]' : 'p-4 w-[180px]'}`}>
                {/* 説明テキスト + キューブ */}
                <div className={`font-medium text-foreground ${isMobile ? 'text-[10px] mb-1' : 'text-sm mb-2'}`}>
                  {productionMode.selectedGoodsIndex !== null
                    ? t.goodsDisplay.selectCell
                    : t.goodsDisplay.selectCube}
                </div>
                <div className={`flex ${isMobile ? 'gap-1 mb-1' : 'gap-2 mb-3'}`}>
                  {productionMode.drawnGoods.map((color, index) => {
                    const isPlaced = productionMode.placedFlags[index];
                    const isSelected = productionMode.selectedGoodsIndex === index;
                    const isClickable = !isPlaced;
                    return (
                      <button
                        key={index}
                        className={`${isMobile ? 'p-0.5' : 'p-1'} rounded ${isClickable ? 'cursor-pointer hover:bg-primary/20' : ''} ${isSelected ? 'bg-primary/30 ring-1 ring-primary' : ''} ${isPlaced ? 'opacity-50' : ''}`}
                        onClick={() => isClickable && productionMode.onGoodsClick(index)}
                        disabled={isPlaced}
                      >
                        <div className="relative">
                          <div className={`w-6 h-6 rounded-sm shadow-sm border ${getGoodsColorClass(color)} ${isSelected ? 'border-primary' : 'border-foreground/20'} ${isPlaced ? 'opacity-50' : ''}`} />
                          {isPlaced && <span className={`absolute inset-0 flex items-center justify-center ${isMobile ? 'text-[8px]' : 'text-xs'} font-bold text-white bg-foreground/40 rounded-sm`}>✓</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* ボタン縦一列 */}
                <div className={`flex flex-col ${isMobile ? 'gap-1' : 'gap-2'}`}>
                  <button className={`btn-primary ${isMobile ? 'text-[15px] px-2 py-1' : 'text-base px-3 py-2'} w-full`} onClick={productionMode.onConfirmPlacement} disabled={productionMode.selectedGoodsIndex === null || !productionMode.selectedCell}>{t.common.confirm}</button>
                  <button className={`btn-ghost ${isMobile ? 'text-[15px] px-2 py-1' : 'text-base px-3 py-2'} w-full`} onClick={productionMode.onCancelPlacement} disabled={productionMode.placedCount === 0 && productionMode.selectedGoodsIndex === null && !productionMode.selectedCell}>{t.common.cancel}</button>
                  <button className={`btn-secondary ${isMobile ? 'text-[15px] px-2 py-1' : 'text-base px-3 py-2'} w-full`} onClick={productionMode.onFinishPlacement} disabled={productionMode.placedCount < 2}>{t.common.finish}</button>
                </div>
              </div>
            ) : null
          ) : (
            // 通常: Dark Side を表示
            <div className={isMobile ? '' : 'space-y-1 md:space-y-2'}>
              <h3 className="font-heading text-xs md:text-sm font-semibold text-foreground bg-foreground/10 text-foreground px-1.5 py-0.5 md:px-2 md:py-1 rounded">
                {t.goodsDisplay.darkSide}
              </h3>

              {/* 6列グリッド: 1-6 の下に E-H（1-4の位置） */}
              <div className="grid grid-cols-6 gap-0.5">
                {/* 都市列 1-6 */}
                {getCityColumns('dark').map((col) => (
                  <CityColumn
                    key={`city-${col.columnNumber}`}
                    side="dark"
                    columnNumber={col.columnNumber}
                    cubes={col.cubes}
                    cityId={col.cityId}
                    productionMode={productionMode}
                    isCompact={isMobile}
                  />
                ))}
                {/* 新都市列 E-H: 1-4の下に配置（ダイス目1-4に対応） */}
                {getNewCityColumns('dark').map((col) => (
                  <NewCityColumn
                    key={`newcity-${col.newCityId}`}
                    side="dark"
                    columnNumber={col.columnNumber}
                    cubes={col.cubes}
                    newCityId={col.newCityId!}
                    productionMode={productionMode}
                    isCompact={isMobile}
                  />
                ))}
                <div className="col-span-2" /> {/* 5-6の下は空 */}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default GoodsDisplayBoard;
